/**
 * Match Persistence Service
 *
 * Ensures users don't lose their funds by persisting active match info
 * across page refreshes, tab closes, and browser restarts.
 */

const ACTIVE_MATCH_KEY = 'solante:active_match';
const MATCH_SIGNATURES_PREFIX = 'solante:signature:';

export interface ActiveMatchInfo {
  matchId: string;
  wallet: string;
  roomId?: string;
  wagerAmount?: string;
  currency?: string;
  joinedAt: number;
  lastSeenAt: number;
}

/**
 * Save the currently active match
 */
export function saveActiveMatch(info: ActiveMatchInfo): void {
  try {
    localStorage.setItem(ACTIVE_MATCH_KEY, JSON.stringify(info));
  } catch (e) {
    console.warn('Failed to save active match:', e);
  }
}

/**
 * Update the last seen timestamp for the active match
 */
export function touchActiveMatch(): void {
  try {
    const stored = localStorage.getItem(ACTIVE_MATCH_KEY);
    if (stored) {
      const info: ActiveMatchInfo = JSON.parse(stored);
      info.lastSeenAt = Date.now();
      localStorage.setItem(ACTIVE_MATCH_KEY, JSON.stringify(info));
    }
  } catch (e) {
    console.warn('Failed to touch active match:', e);
  }
}

/**
 * Get the currently active match (if any)
 */
export function getActiveMatch(): ActiveMatchInfo | null {
  try {
    const stored = localStorage.getItem(ACTIVE_MATCH_KEY);
    if (!stored) return null;

    const info: ActiveMatchInfo = JSON.parse(stored);

    // Check if match is too old (more than 30 minutes since last seen)
    // This prevents stale matches from blocking new ones
    const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
    if (Date.now() - info.lastSeenAt > MAX_AGE_MS) {
      clearActiveMatch();
      return null;
    }

    return info;
  } catch (e) {
    console.warn('Failed to get active match:', e);
    return null;
  }
}

/**
 * Clear the active match (when match completes or user leaves)
 */
export function clearActiveMatch(): void {
  try {
    localStorage.removeItem(ACTIVE_MATCH_KEY);
  } catch (e) {
    console.warn('Failed to clear active match:', e);
  }
}

/**
 * Check if user has an active match that needs reconnection
 */
export function hasActiveMatchForWallet(wallet: string): boolean {
  const match = getActiveMatch();
  return match !== null && match.wallet === wallet;
}

/**
 * Save join signature for a match
 * Uses sessionStorage instead of localStorage to reduce XSS risk -
 * signatures are session-scoped and don't persist across tabs
 */
export function saveJoinSignature(matchId: string, wallet: string, signature: string): void {
  try {
    const key = `${MATCH_SIGNATURES_PREFIX}${matchId}:${wallet}`;
    sessionStorage.setItem(key, signature);
  } catch (e) {
    console.warn('Failed to save join signature:', e);
  }
}

/**
 * Get join signature for a match
 */
export function getJoinSignature(matchId: string, wallet: string): string | null {
  try {
    const key = `${MATCH_SIGNATURES_PREFIX}${matchId}:${wallet}`;
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

/**
 * Clear join signature for a match
 */
export function clearJoinSignature(matchId: string, wallet: string): void {
  try {
    const key = `${MATCH_SIGNATURES_PREFIX}${matchId}:${wallet}`;
    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to clear join signature:', e);
  }
}

/**
 * Clean up old signatures (call periodically)
 */
export function cleanupOldSignatures(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(MATCH_SIGNATURES_PREFIX)) {
        // Could add timestamp-based cleanup here if needed
        keysToRemove.push(key);
      }
    }
    // Keep only recent signatures (last 10)
    if (keysToRemove.length > 10) {
      keysToRemove.slice(0, -10).forEach((key) => sessionStorage.removeItem(key));
    }
  } catch (e) {
    console.warn('Failed to cleanup signatures:', e);
  }
}
